import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Smartphone, Handshake } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { companyInfo } from '../data/mock';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-white">
      {/* Main Footer Content - hellgrauer Hintergrund, bündig mit Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
        <div className="bg-gray-100 rounded-lg py-12 px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* Links: Kontakt */}
            <div>
              <h3 className="text-gray-900 font-extrabold text-xl mb-6">{t('footer.contact')}</h3>
              {/* Contact Info - Icons bleiben rot */}
              <div className="space-y-3">
                <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="flex items-center text-sm font-medium text-gray-900 hover:text-red-600 transition-colors">
                  <Phone size={16} className="mr-2 text-red-600" />
                  {companyInfo.phone}
                </a>
                <a href={`tel:${companyInfo.mobile.replace(/\s/g, '')}`} className="flex items-center text-sm font-medium text-gray-900 hover:text-red-600 transition-colors">
                  <Smartphone size={16} className="mr-2 text-red-600" />
                  {companyInfo.mobile}
                </a>
                <a href={`mailto:${companyInfo.email}`} className="flex items-center text-sm font-medium text-gray-900 hover:text-red-600 transition-colors">
                  <Mail size={16} className="mr-2 text-red-600" />
                  {companyInfo.email}
                </a>
                <div className="flex items-start text-sm font-medium text-gray-900">
                  <MapPin size={16} className="mr-2 mt-0.5 text-red-600 flex-shrink-0" />
                  <span>{companyInfo.address}</span>
                </div>
              </div>
            </div>

            {/* Mitte: Rechtliches & Service */}
            <div className="flex flex-col sm:flex-row justify-center gap-8 lg:gap-16">
              {/* Rechtliches */}
              <div>
                <h3 className="text-gray-900 font-extrabold text-xl mb-6">{t('footer.legal')}</h3>
                <ul className="space-y-3">
                  <li>
                    <Link to="/impressum" className="text-sm font-medium text-gray-900 hover:text-red-600 transition-colors flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                      {t('footer.impressum')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/datenschutz" className="text-sm font-medium text-gray-900 hover:text-red-600 transition-colors flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                      {t('footer.datenschutz')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/cookie-richtlinie" className="text-sm font-medium text-gray-900 hover:text-red-600 transition-colors flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                      {t('footer.cookiePolicy')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/datenschutz-social-media" className="text-sm font-medium text-gray-900 hover:text-red-600 transition-colors flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                      {t('footer.datenschutzSocial')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/agb" className="text-sm font-medium text-gray-900 hover:text-red-600 transition-colors flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                      AGB
                    </Link>
                  </li>
                  <li>
                    <Link to="/widerrufsbelehrung" className="text-sm font-medium text-gray-900 hover:text-red-600 transition-colors flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                      Widerrufsbelehrung
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Service */}
              <div>
                <h3 className="text-gray-900 font-extrabold text-xl mb-6">{t('footer.service')}</h3>
                <ul className="space-y-3">
                  <li>
                    <Link to="/kontakt" className="text-sm font-medium text-gray-900 hover:text-red-600 transition-colors flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                      {t('footer.contact')}
                    </Link>
                  </li>
                  <li>
                    <a 
                      href={`https://wa.me/49${companyInfo.mobile.replace(/\s/g, '').replace(/^0/, '')}?text=Hallo%20Jannesmann%20Baumaschinen%2C%20ich%20habe%20eine%20Frage.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-gray-900 hover:text-red-600 transition-colors flex items-center"
                    >
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                      {t('footer.whatsappChat')}
                    </a>
                  </li>
                  <li>
                    <Link to="/garantiebedingungen" className="text-sm font-medium text-gray-900 hover:text-red-600 transition-colors flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                      {t('footer.garantie')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/faq" className="text-sm font-medium text-gray-900 hover:text-red-600 transition-colors flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                      FAQ
                    </Link>
                  </li>
                  <li>
                    <Link to="/gewaehrleistung" className="text-sm font-medium text-gray-900 hover:text-red-600 transition-colors flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                      Gewährleistung
                    </Link>
                  </li>
                  <li>
                    <Link to="/zahlungsarten" className="text-sm font-medium text-gray-900 hover:text-red-600 transition-colors flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-2"></span>
                      Zahlungsarten
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            {/* Rechts: Folgen Sie uns */}
            <div className="flex flex-col items-start md:items-end">
              <h3 className="text-gray-900 font-extrabold text-xl mb-6">{t('footer.followUs')}</h3>
              {/* Social Media Icons */}
              <div className="flex space-x-3">
                {/* Facebook */}
                <a 
                  href="https://facebook.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white border border-gray-200 hover:bg-red-600 hover:border-red-600 hover:text-white text-gray-700 rounded-full flex items-center justify-center transition-all shadow-sm"
                  aria-label="Facebook"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                
                {/* Instagram */}
                <a 
                  href="https://instagram.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white border border-gray-200 hover:bg-red-600 hover:border-red-600 hover:text-white text-gray-700 rounded-full flex items-center justify-center transition-all shadow-sm"
                  aria-label="Instagram"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
                
                {/* YouTube */}
                <a 
                  href="https://youtube.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white border border-gray-200 hover:bg-red-600 hover:border-red-600 hover:text-white text-gray-700 rounded-full flex items-center justify-center transition-all shadow-sm"
                  aria-label="YouTube"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
                
                {/* TikTok */}
                <a 
                  href="https://tiktok.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white border border-gray-200 hover:bg-red-600 hover:border-red-600 hover:text-white text-gray-700 rounded-full flex items-center justify-center transition-all shadow-sm"
                  aria-label="TikTok"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                </a>
              </div>
              
              {/* Partner werden - unter den Social Icons, zentriert */}
              <Link 
                to="/partner-werden"
                className="flex items-center mt-4 text-gray-700 hover:text-red-600 transition-colors"
                aria-label="Partner werden"
              >
                <div className="w-10 h-10 bg-white border border-gray-200 hover:bg-red-600 hover:border-red-600 hover:text-white rounded-full flex items-center justify-center transition-all shadow-sm mr-2">
                  <Handshake className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium">Partner werden</span>
              </Link>
            </div>
          </div>

          {/* Zahlungsmethoden */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-gray-900 font-extrabold text-lg mb-4 text-center">{t('footer.paymentMethods')}</h3>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {/* Visa - Inline SVG für Zuverlässigkeit */}
              <div className="bg-white rounded-lg border border-gray-200 px-4 py-2 flex items-center justify-center h-14 w-20" title="Visa">
                <svg viewBox="0 0 750 471" className="h-8 w-auto">
                  <path d="M278.198 334.228l33.36-195.763h53.358l-33.384 195.763h-53.334zm246.11-191.54c-10.57-3.966-27.135-8.222-47.822-8.222-52.725 0-89.863 26.551-90.18 64.604-.297 28.129 26.515 43.822 46.754 53.185 20.77 9.597 27.752 15.716 27.654 24.283-.133 13.123-16.586 19.116-31.924 19.116-21.355 0-32.701-2.967-50.225-10.274l-6.878-3.111-7.487 43.822c12.463 5.466 35.508 10.199 59.438 10.445 56.09 0 92.502-26.248 92.916-66.884.199-22.27-14.016-39.216-44.801-53.188-18.65-9.056-30.072-15.099-29.951-24.269 0-8.137 9.668-16.838 30.56-16.838 17.446-.271 30.089 3.527 39.936 7.496l4.781 2.26 7.229-42.425m137.307-4.223h-41.232c-12.773 0-22.332 3.486-27.94 16.234l-79.245 179.404h56.031s9.159-24.121 11.231-29.418c6.123 0 60.555.084 68.336.084 1.596 6.854 6.492 29.334 6.492 29.334h49.512l-43.185-195.638zm-65.417 126.408c4.414-11.279 21.26-54.724 21.26-54.724-.314.521 4.381-11.334 7.074-18.684l3.607 16.878s10.217 46.729 12.352 56.527h-44.293v.003zm-363.667-126.408l-52.24 133.496-5.567-27.129c-9.726-31.274-40.025-65.157-73.898-82.12l47.767 171.203 56.455-.063 84.004-195.386-56.521-.001" fill="#1A1F71"/>
                  <path d="M146.92 138.465H60.879l-.682 4.073c66.939 16.204 111.232 55.363 129.618 102.415l-18.709-89.96c-3.229-12.396-12.597-16.095-24.186-16.528" fill="#F9A533"/>
                </svg>
              </div>
              
              {/* Mastercard - Neues offizielles Logo */}
              <div className="bg-white rounded-lg border border-gray-200 px-4 py-2 flex items-center justify-center h-14 w-20" title="Mastercard">
                <img src="https://customer-assets.emergentagent.com/job_c8ae81ef-39b2-4170-b30f-e165111bc7d2/artifacts/gwxfm1bl_MasterCard_Logo.svg.png" alt="Mastercard" className="h-8 w-auto object-contain" />
              </div>
              
              {/* American Express - Korrektes AMEX Logo */}
              <div className="bg-white rounded-lg border border-gray-200 px-4 py-2 flex items-center justify-center h-14 w-20" title="American Express">
                <svg viewBox="0 0 60 40" className="h-8 w-auto">
                  <rect width="60" height="40" rx="4" fill="#016FD0"/>
                  <text x="30" y="26" textAnchor="middle" fill="white" fontSize="12" fontFamily="Arial, sans-serif" fontWeight="bold">AMEX</text>
                </svg>
              </div>
              
              {/* Apple Pay - Mollie CDN */}
              <div className="bg-white rounded-lg border border-gray-200 px-4 py-2 flex items-center justify-center h-14 w-20" title="Apple Pay">
                <img src="https://www.mollie.com/external/icons/payment-methods/applepay.svg" alt="Apple Pay" className="h-8 w-auto" onError={(e) => e.target.style.display='none'} />
              </div>
              
              {/* PayPal - Neues offizielles Logo */}
              <div className="bg-white rounded-lg border border-gray-200 px-4 py-2 flex items-center justify-center h-14 w-20" title="PayPal">
                <img src="https://customer-assets.emergentagent.com/job_c8ae81ef-39b2-4170-b30f-e165111bc7d2/artifacts/529c93tl_Paypal_2014_logo.png" alt="PayPal" className="h-8 w-auto object-contain" />
              </div>
              
              {/* Klarna - Mollie CDN */}
              <div className="bg-white rounded-lg border border-gray-200 px-4 py-2 flex items-center justify-center h-14 w-20" title="Klarna">
                <img src="https://www.mollie.com/external/icons/payment-methods/klarna.svg" alt="Klarna" className="h-8 w-auto" onError={(e) => e.target.style.display='none'} />
              </div>
              
              {/* SEPA Bank Transfer - Mollie CDN */}
              <div className="bg-white rounded-lg border border-gray-200 px-4 py-2 flex items-center justify-center h-14 w-20" title="SEPA Überweisung">
                <img src="https://www.mollie.com/external/icons/payment-methods/banktransfer.svg" alt="SEPA" className="h-8 w-auto" onError={(e) => e.target.style.display='none'} />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar - nur Copyright, auch innerhalb des Containers */}
        <div className="py-6">
          <p className="text-sm font-medium text-gray-600 text-center">
            © {currentYear} {companyInfo.name}. {t('footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
